/**
 * Enemy — a side-scrolling foe.
 *
 * Enemies drift leftward across the screen at a level-dependent speed and
 * sway vertically with a sine wave to give them some life. Each level picks
 * one of two texture kinds: cardboard (level 1+2) or aluminum can (level 3+).
 * On death they explode and add to the player's score.
 *
 * Per the user's design (2026-08-04): only two enemy shapes remain in the
 * game. The previous "plastic" variants have been removed entirely — every
 * enemy is now a plain white-rectangle placeholder, replaceable by the user
 * via `data/graphics_user/<key>.png`.
 */
import Phaser from "phaser";

export type EnemyKind =
  | "biowaste"
  | "cardboard"
  | "can"
  | "plastic"
  | "ewaste"
  | "glass"
  | "toxic"
  | "radioactive"
  | "tire"
  | "battery"
  | "rareearth"
  | "satellite"
  | "chemicals"
  | "boss_minion"; // reserved for boss-spawned adds

interface EnemyOpts {
  kind: EnemyKind;
  textures?: string[];
  score?: number;
  hp?: number;
  speed?: number;
  swayAmp?: number;
  swayHz?: number;
  scale?: number;
}

const KIND_DEFAULTS: Record<EnemyKind, Omit<EnemyOpts, "kind">> = {
  biowaste:    { textures: ["enemy_biowaste_1", "enemy_biowaste_2"], score: 5, hp: 1, speed: 90, swayAmp: 20, swayHz: 0.5 },
  cardboard:   { textures: ["enemy_cardboard_1", "enemy_cardboard_2"], score: 10, hp: 1, speed: 110, swayAmp: 28, swayHz: 0.6 },
  can:         { textures: ["enemy_can_1", "enemy_can_2"], score: 15, hp: 2, speed: 140, swayAmp: 32, swayHz: 0.8 },
  plastic:     { textures: ["enemy_plastic_1", "enemy_plastic_2"], score: 30, hp: 2, speed: 120, swayAmp: 20, swayHz: 1.0 },
  ewaste:      { textures: ["enemy_ewaste_1"], score: 40, hp: 3, speed: 130, swayAmp: 10, swayHz: 1.2 },
  glass:       { textures: ["enemy_glass_1"], score: 50, hp: 1, speed: 160, swayAmp: 40, swayHz: 1.5 },
  toxic:       { textures: ["enemy_toxic_1"], score: 60, hp: 4, speed: 100, swayAmp: 20, swayHz: 0.8 },
  radioactive: { textures: ["enemy_radioactive_1"], score: 80, hp: 5, speed: 110, swayAmp: 25, swayHz: 0.9 },
  tire:        { textures: ["enemy_tire_1"], score: 100, hp: 6, speed: 200, swayAmp: 100, swayHz: 2.0 },
  battery:     { textures: ["enemy_battery_1"], score: 120, hp: 4, speed: 140, swayAmp: 15, swayHz: 1.0 },
  rareearth:   { textures: ["enemy_rareearth_1"], score: 150, hp: 8, speed: 100, swayAmp: 5, swayHz: 0.5 },
  satellite:   { textures: ["enemy_satellite_1"], score: 200, hp: 3, speed: 180, swayAmp: 60, swayHz: 1.0 },
  chemicals:   { textures: ["enemy_chemicals_1"], score: 300, hp: 10, speed: 120, swayAmp: 30, swayHz: 1.5 },
  boss_minion: { textures: ["enemy_cardboard_1"], score: 5, hp: 1, speed: 220, swayAmp: 24, swayHz: 1.4 },
};

export class Enemy extends Phaser.Physics.Arcade.Sprite {
  public kind: EnemyKind;
  public scoreValue = 10;
  public maxHp = 1;
  public hp = 1;
  private speed = 110;
  private swayAmp = 28;
  private swayHz = 0.6;
  private baseY = 0;
  private bornAt = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, opts?: Partial<EnemyOpts>) {
    const o: Partial<EnemyOpts> = opts ?? {};
    // When Phaser's PhysicsGroup auto-spawns pooled instances, `opts` is
    // undefined — fall back to a cardboard enemy at the origin, marked
    // inactive. GameScene.enableBody() resets the right texture/HB when
    // it pulls the sprite from the pool.
    const def = KIND_DEFAULTS[o.kind ?? "cardboard"] ?? {};
    const availableTextures: string[] = [];
    for (let i = 1; i <= 40; i++) {
        const tex = `enemy_${o.kind ?? "cardboard"}_${i}`;
        if (scene.textures.exists(tex)) {
            availableTextures.push(tex);
        }
    }
    if (availableTextures.length === 0) {
        availableTextures.push("enemy_cardboard_1");
    }
    const texture = availableTextures[Math.floor(Math.random() * availableTextures.length)];
    super(scene, x, y, texture);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    const body = this.body as Phaser.Physics.Arcade.Body;
    const w = this.width;
    const h = this.height;
    body.setSize(w * 0.8, h * 0.8);
    body.setOffset(w * 0.1, h * 0.1);
    this.kind = o.kind ?? "cardboard";
    this.scoreValue = o.score ?? def.score ?? 10;
    this.maxHp = o.hp ?? def.hp ?? 1;
    this.hp = this.maxHp;
    this.speed = o.speed ?? def.speed ?? 110;
    this.swayAmp = o.swayAmp ?? def.swayAmp ?? 28;
    this.swayHz = o.swayHz ?? def.swayHz ?? 0.6;
    if (o.scale !== undefined) this.setScale(o.scale);
    this.baseY = y;
    this.bornAt = scene.time.now;
  }

  /**
   * Pre-existing override hooks. The Phaser pool may instantiate enemies
   * with no opts; these setters let GameScene reset the per-sprite state
   * when it pulls one from the pool.
   */
  public setKind(kind: EnemyKind): void {
    const d = KIND_DEFAULTS[kind];
    const availableTextures: string[] = [];
    for (let i = 1; i <= 40; i++) {
        const tex = `enemy_${kind}_${i}`;
        if (this.scene.textures.exists(tex)) {
            availableTextures.push(tex);
        }
    }
    if (availableTextures.length === 0) {
        availableTextures.push("enemy_cardboard_1");
    }
    const texture = availableTextures[Math.floor(Math.random() * availableTextures.length)];
    this.setTexture(texture);
    const body = this.body as Phaser.Physics.Arcade.Body;
    const w = this.width;
    const h = this.height;
    body.setSize(w * 0.8, h * 0.8);
    body.setOffset(w * 0.1, h * 0.1);
    this.kind = kind;
    this.scoreValue = d.score ?? 10;
    this.maxHp = d.hp ?? 1;
    this.hp = this.maxHp;
    this.speed = d.speed ?? 110;
    this.swayAmp = d.swayAmp ?? 28;
    this.swayHz = d.swayHz ?? 0.6;
    this.isShooter = false;
    this.lastShooterShotAt = 0;
    this.isOrbiting = false;
    this.orbitAngle = 0;
    this.orbitRadius = 110;
  }

  public isShooter = false;
  public lastShooterShotAt = 0;
  public isOrbiting = false;
  public orbitAngle = 0;
  public orbitRadius = 110;

  public setShooter(shooter: boolean): void {
    this.isShooter = shooter;
    this.lastShooterShotAt = 0;
  }

  public setOrbiting(orbiting: boolean, angle = 0, radius = 110): void {
    this.isOrbiting = orbiting;
    this.orbitAngle = angle;
    this.orbitRadius = radius;
  }

  public movement(now: number): number {
    return Math.sin((now - this.bornAt) / 1000 * this.swayHz * 2 * Math.PI) * this.swayAmp;
  }

  public getBaseY(): number {
    return this.baseY;
  }

  public setBaseY(y: number): void {
    this.baseY = y;
  }

  public getSpeed(): number {
    return this.speed;
  }

  /** Returns true if HP is exhausted; the caller drops the enemy. */
  public damage(amount: number): boolean {
    this.hp -= amount;
    return this.hp <= 0;
  }
}
